import React from "react";
import { Typography } from "@mui/material";
import DocumentCard from "@components/DocumentCard/DocumentCard";
import Paper from "@components/Paper/Paper";
import PaperItem from "@components/Paper/PaperItem";
import type { SideDrawerAttachments } from "@services/movement-services/types";
import { useTranslation } from "react-i18next";

const FIELD_LABEL_CLASS = "shrink-0 font-normal text-[#646A77]";
const VALUE_CLASS = "min-w-0 flex-1 font-semibold text-[#111827]";

interface TerminalInformationProps {
  title: string;
  note?: string | null;
  attachments?: SideDrawerAttachments[] | null;
  berthPlan?: SideDrawerAttachments[] | null;
  unberthPlan?: SideDrawerAttachments[] | null;
  testId: string;
  onDownload: (attachment: SideDrawerAttachments) => void;
  onPreview: (attachment: SideDrawerAttachments) => void;
}

const TerminalInformation: React.FC<TerminalInformationProps> = ({
  title,
  note,
  attachments,
  berthPlan,
  unberthPlan,
  testId,
  onDownload,
  onPreview,
}) => {
  const { t } = useTranslation();
  const hasNote = Boolean(note?.trim());

  if (!hasNote && !attachments?.length && !berthPlan?.length && !unberthPlan?.length) return null;

  return (
    <Paper variant="outlined">
      <div className="flex flex-col gap-3" data-testid={testId}>
        <Typography
          variant="subtitle1"
          className="font-bold"
          sx={{ fontWeight: 600, color: "#111827" }}
        >
          {title}
        </Typography>
        <div className="flex flex-col gap-4">
          {hasNote && (
            <PaperItem
              testId={`${testId}-message`}
              translate={t("vessel-call.movement-services.preview.details.notes-to-agent")}
              text={note}
              textStyles={VALUE_CLASS}
              tranStyle={FIELD_LABEL_CLASS}
              wrapperStyle="gap-4 pt-3"
              labelClassName={FIELD_LABEL_CLASS}
              valueClassName={VALUE_CLASS}
            />
          )}
          {Boolean(attachments?.length) && (
            <Typography
              component="div"
              variant="body1"
              data-testid={`${testId}-attachments`}
              className={`min-w-0 grid grid-cols-2 gap-4${hasNote ? " border-t border-[#F1F2F4] pt-3" : ""}`}
            >
              {attachments?.map((attachment, index) => (
                <DocumentCard
                  key={`${attachment.url}-${index}`}
                  fileName={attachment.originalFileName || "-"}
                  onDownload={() => onDownload(attachment)}
                  onPreview={() => onPreview(attachment)}
                />
              ))}
            </Typography>
          )}
          {Boolean(berthPlan?.length) && (
            <Typography
              component="div"
              variant="body1"
              data-testid={`${testId}-berth-plan`}
              className={`min-w-0 grid grid-cols-2 gap-4${hasNote || attachments?.length ? " border-t border-[#F1F2F4] pt-3" : ""}`}
            >
              {berthPlan?.map((document, index) => (
                <DocumentCard
                  key={`${document.url}-${index}`}
                  title={t("vessel-call.movement-services.preview.documents.berth-plan")}
                  fileName={document.originalFileName || "-"}
                  onDownload={() => onDownload(document)}
                  onPreview={() => onPreview(document)}
                />
              ))}
            </Typography>
          )}
          {Boolean(unberthPlan?.length) && (
            <Typography
              component="div"
              variant="body1"
              data-testid={`${testId}-unberth-plan`}
              className={`min-w-0 grid grid-cols-2 gap-4${hasNote || attachments?.length ? " border-t border-[#F1F2F4] pt-3" : ""}`}
            >
              {unberthPlan?.map((document, index) => (
                <DocumentCard
                  key={`${document.url}-${index}`}
                  title={t("vessel-call.movement-services.preview.documents.unberth-plan")}
                  fileName={document.originalFileName || "-"}
                  onDownload={() => onDownload(document)}
                  onPreview={() => onPreview(document)}
                />
              ))}
            </Typography>
          )}
        </div>
      </div>
    </Paper>
  );
};

export default TerminalInformation;
